import useSWR from 'swr';
import { Enrollment } from 'models/CommonType';
import { fetcherWithToken } from './ApiClient';

// API Response types
export interface EnrollmentResponse {
  results: Enrollment[];
  meta: {
    pagination: {
      total: number;
      pages: number;
      page: number;
      per_page: number;
      count?: number;
    };
  };
  links: {
    first: string;
    last: string;
    next: string | null;
    prev: string | null;
  };
}

export interface EnrollmentRankingsResponse {
  results: Enrollment[];
  meta: {
    pagination: {
      total: number;
      pages: number;
      page: number;
      per_page: number;
    };
  };
}

// API functions
const fetchEnrollment = async (campaignId: string, userId: string, authKey: string): Promise<Enrollment | null> => {
  const queryParams = new URLSearchParams({
    campaign: campaignId.toLowerCase(),
    user: userId
  });

  const url = `${process.env.REACT_APP_DOMAIN}/api/enrollments/?${queryParams.toString()}`;
  const response: EnrollmentResponse = await fetcherWithToken(url, authKey);

  // Extract the first result from the API response
  return response.results && response.results.length > 0 ? response.results[0] : null;
};

const fetchEnrollmentRankings = async (
  campaignId: string,
  page: number,
  pageSize: number,
  authKey: string
): Promise<EnrollmentRankingsResponse> => {
  const queryParams = new URLSearchParams({
    campaign: campaignId.toLowerCase(),
    sort: '-miles,id',
    is_participant_only: 'true',
    'page[number]': page.toString(),
    'page[size]': pageSize.toString()
  });

  const url = `${process.env.REACT_APP_DOMAIN}/api/enrollments/?${queryParams.toString()}`;
  const response = await fetcherWithToken(url, authKey);

  // Handle different response formats
  if (response.results && response.meta && response.meta.pagination) {
    // Assign total from count if available
    const count = response.meta.pagination.count;
    if (typeof count === 'number') {
      response.meta.pagination.total = count;
    }
    return response;
  } else if (Array.isArray(response)) {
    // If response is a direct array, wrap it in the expected format
    return {
      results: response,
      meta: {
        pagination: {
          total: response.length,
          pages: Math.ceil(response.length / pageSize),
          page: page,
          per_page: pageSize
        }
      }
    };
  }

  // If response is object with meta/pagination but missing results
  if (response && response.meta && response.meta.pagination) {
    const count = response.meta.pagination.count;
    response.meta.pagination.total = typeof count === 'number' ? count : 0;
  }

  return response;
};

const fetchJoinedCampaigns = async (
  userId: string,
  sortBy: string,
  page: number,
  isExpired?: boolean | null,
  authKey?: string
): Promise<EnrollmentResponse> => {
  const queryParams = new URLSearchParams({
    user: userId.toLowerCase(),
    sort: sortBy,
    'page[number]': page.toString()
  });

  if (isExpired !== undefined && isExpired !== null) {
    queryParams.set('campaign__is_expired', isExpired.toString());
  }

  const url = `${process.env.REACT_APP_DOMAIN}/api/enrollments/?${queryParams.toString()}`;
  const response = await fetcherWithToken(url, authKey || '');

  // Handle different response formats
  if (response.results && response.meta && response.meta.pagination) {
    const count = response.meta.pagination.count;
    if (typeof count === 'number') {
      response.meta.pagination.total = count;
    }
    return response;
  }

  return response;
};

// Custom hooks
export const useEnrollment = (campaignId: string, userId: string, authKey: string) => {
  const shouldFetch = campaignId && userId && authKey;

  const { data, error, mutate } = useSWR(
    shouldFetch ? [`enrollment`, campaignId, userId] : null,
    () => fetchEnrollment(campaignId, userId, authKey),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  return {
    data, // This is now Enrollment | null instead of the full API response
    isLoading: !error && !data && shouldFetch,
    isError: error,
    mutate,
  };
};

export const useEnrollmentRankings = (
  campaignId: string,
  page: number,
  pageSize: number,
  authKey: string
) => {
  const shouldFetch = campaignId && authKey;

  const { data, error, mutate } = useSWR(
    shouldFetch ? [`rankings`, campaignId, page, pageSize] : null,
    () => fetchEnrollmentRankings(campaignId, page, pageSize, authKey),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  return {
    data,
    isLoading: !error && !data && shouldFetch,
    isError: error,
    mutate,
  };
};

export const useJoinedCampaigns = (
  userId: string,
  sortBy: string = '-campaign__campaign_order',
  page: number = 1,
  isExpired?: boolean | null,
  authKey?: string
) => {
  const shouldFetch = userId && authKey;

  const { data, error, mutate } = useSWR(
    shouldFetch ? [`joinedCampaigns`, userId, sortBy, page, isExpired] : null,
    () => fetchJoinedCampaigns(userId, sortBy, page, isExpired, authKey),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  return {
    data,
    isLoading: !error && !data && shouldFetch,
    isError: error,
    mutate,
  };
};